import {
  trpcIdentityGetOrganization,
  trpcIdentityGetOrganizationOpen,
} from '@trashlab/api/src/generated/hooks';
import { useMemo, useSyncExternalStore } from 'react';
import {
  getScenarioLabControls,
  isScenarioLabEnabled,
  subscribeScenarioLabControls,
} from '@trashlab/viteapp/src/scenario-lab/scenario-lab.controls';
import {
  useAppSession,
  useAppSessionSafe,
  type OrganizationData,
} from './app-session-context';

// Re-export the type for external consumers
export type { OrganizationData };

type OrganizationOpenData = NonNullable<
  ReturnType<typeof trpcIdentityGetOrganizationOpen.useQuery>['data']
>;

/**
 * Hook to fetch organization data for public/open endpoints.
 * Returns undefined while loading - callers must handle loading state.
 *
 * Note: This hook is for PUBLIC pages that don't have access to AppSessionProvider.
 * For authenticated pages, use useTrashlabOrganization() instead.
 */
export const useTrashlabOrganizationOpen = ({
  tenantId,
}: {
  tenantId: string;
}): OrganizationOpenData | undefined => {
  const organizationQuery = trpcIdentityGetOrganizationOpen.useQuery({
    tenantId,
  });

  return organizationQuery.data;
};

/**
 * Hook to fetch authenticated organization data.
 * Returns guaranteed data when inside AppSessionProvider (authenticated routes).
 *
 * The provider ensures data is loaded before rendering children,
 * so consumers don't need undefined checks.
 *
 * @throws Error if used outside of AppSessionProvider
 *
 * @example
 * ```tsx
 * function OrgSettings() {
 *   const organization = useTrashlabOrganization();
 *   // No undefined check needed - data is guaranteed
 *   return <div>Settings for {organization.name}</div>;
 * }
 * ```
 */
export const useTrashlabOrganization = (): OrganizationData => {
  const { organization } = useAppSession();
  const controls = useSyncExternalStore(
    subscribeScenarioLabControls,
    getScenarioLabControls,
    getScenarioLabControls,
  );
  const scenarioEnabled = isScenarioLabEnabled();

  return useMemo(() => {
    if (!scenarioEnabled || !controls.timeZone) return organization;
    return { ...organization, timezone: controls.timeZone };
  }, [controls.timeZone, organization, scenarioEnabled]);
};

/**
 * Async hook that returns the full organization query object.
 * Useful for consumers who need access to refetch or other query methods.
 *
 * Reads from AppSessionContext when available (preferred in authenticated routes)
 * and falls back to direct query when context is not available.
 *
 * @param enabled - Whether to enable the query (default: true). Only applies to fallback.
 */
export const useTrashlabOrganizationAsync = (enabled = true) => {
  // Try to read from context first (preferred when inside AppSessionProvider)
  const sessionContext = useAppSessionSafe();

  // Fall back to direct query when outside AppSessionProvider
  const fallbackQuery = trpcIdentityGetOrganization.useQuery(undefined, {
    enabled: enabled && !sessionContext,
  });

  // Return context query if available, otherwise fall back to direct query
  return sessionContext?.organizationQuery ?? fallbackQuery;
};

export enum QuickBooksService {
  conductorQB = 'conductorQB',
  qbo = 'qbo',
  none = 'none',
}

/**
 * Hook to get organization data with QuickBooks service type.
 *
 * @example
 * ```tsx
 * function AccountingSettings() {
 *   const { organization, quickbooksService } = useTrashlabOrganizationQuickbooks();
 *
 *   if (quickbooksService === QuickBooksService.none) {
 *     return <SetupQuickBooks />;
 *   }
 *
 *   return <QuickBooksIntegration service={quickbooksService} />;
 * }
 * ```
 */
export const useTrashlabOrganizationQuickbooks = () => {
  const organization = useTrashlabOrganization();

  let quickbooksService = QuickBooksService.none;
  if (organization.quickbooksEnabled) {
    quickbooksService = QuickBooksService.qbo;
  } else if (organization.qbConductorEnabled) {
    quickbooksService = QuickBooksService.conductorQB;
  }

  return { organization, quickbooksService };
};
